import _ from 'lodash';
import React, { useState, useContext, useEffect } from 'react';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Validator } from 'blaze-validator';
import ClipLoader from 'react-spinners/ClipLoader';
import { modal } from '../modal/ModalEmitter';
import FileNotFound from '../FileNotFound';
import LoyaltyContext from './LoyaltyContext';
import ModalContext from '../modal/ModalContext';

const OfferPartList = ({ initialCustomerId = '', initialOffersSelected = [], initialSearch = {}, onSelect }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState({});

  const { fetchPublicOffers, fetchCustomerOffers } = useContext(LoyaltyContext);
  const { setTitle } = useContext(ModalContext);
  const selectedCount = Object.keys(selected).length;

  const includeApplicableParts = offers => {
    // User did not narrow down parts
    if (!initialSearch.partNumbers) {
      return [];
    }

    for (const offer of offers) {
      const partsOnOffer = offer.PartNumbers;
      const searchedParts = initialSearch.partNumbers
        ? initialSearch.partNumbers.split(',').map(p => p.toUpperCase())
        : [];

      // Find part from query that matches one in list of offers
      const applicableParts = partsOnOffer.filter(p => {
        return searchedParts.includes(p.toUpperCase());
      });

      offer.ApplicableParts = applicableParts;
    }

    const offerPerPart = [];
    for (const offer of offers) {
      if (!offer.ApplicableParts || !offer.ApplicableParts.length) {
        continue;
      }

      // Business Logic per cervus?  DO NOT INCLUDE OPC offers
      if (offer.OfferType !== 'Monthly Parts' && offer.OfferType !== 'Yearly/Annual Parts') {
        continue;
      }

      for (const part of offer.ApplicableParts) {
        offerPerPart.push({ ...offer, OfferPartId: `${offer.OfferUniqueId}:${part}`, SelectedPart: part });
      }

      // console.log(offer);
    }

    // Business Logic.  Only show hightest amount offer per part and prioritize Monthly over Yearly
    const sortedOffers = _.orderBy(offerPerPart, ['Amount', 'OfferType'], ['desc', 'asc']);
    const final = [];

    initialSearch.partNumbers.split(',').forEach(partNum => {
      for (const sortedOffer of sortedOffers) {
        if (sortedOffer.SelectedPart === partNum) {
          final.push(sortedOffer);
          return;
        }
      }
    });

    return final;
  };

  const toggleRow = offer => {
    const offerId = `${offer.OfferUniqueId}:${offer.SelectedPart}`;
    const newSelected = { ...selected };

    if (selected[offerId]) {
      delete newSelected[offerId];
    } else {
      // Check for one unique offer by part
      // 5296,C81-6022,C81-1013,02-01846,JA11100,E006-185  (5296 on multiple offers)
      const selectedOffers = Object.keys(selected).map(offerId => selected[offerId]);

      const offerWithSamePart = selectedOffers.find(selectedOffer => {
        if (selectedOffer.SelectedPart === offer.SelectedPart) {
          return selectedOffer;
        }

        return null;
      });

      if (offerWithSamePart) {
        const selectedOfferId = `${offerWithSamePart.OfferUniqueId}:${offerWithSamePart.SelectedPart}`;
        if (newSelected[selectedOfferId]) {
          delete newSelected[selectedOfferId];
        }
      }

      newSelected[offerId] = offer;
    }

    setSelected(newSelected);
  };

  const getSelectedAmount = () => {
    let amount = 0;
    if (!selectedCount) {
      return amount;
    }

    Object.keys(selected).forEach(offerId => {
      const offer = selected[offerId];
      amount += offer.Amount;
    });

    return amount;
  };

  const columns = [
    {
      id: 'checkbox',
      accessor: '',
      Cell: row => {
        return (
          <input
            type="checkbox"
            className="checkbox"
            checked={!!selected[`${row.original.OfferUniqueId}:${row.original.SelectedPart}`]}
            onChange={() => {
              toggleRow(row.original);
            }}
          />
        );
      },
      sortable: false,
      width: 30,
      className: 'text-center sticky',
      Header: '',
      headerClassName: 'sticky',
    },
    {
      Header: '',
      width: 50,
      className: 'text-center',
      Cell: row => {
        return (
          <ButtonGroup>
            <Button
              onClick={e => {
                e.stopPropagation();
                modal.open('loyaltyOffer', { offerId: row.original.OfferUniqueId, headerClass: 'primary' });
              }}
              size="xs"
              title="View Offer"
            >
              View
            </Button>
          </ButtonGroup>
        );
      },
    },
    { Header: 'Offer ID', accessor: 'OfferUniqueId', show: false },
    {
      Header: 'Amount',
      accessor: 'Amount',
      className: 'amount',
      Cell: row => {
        return (
          <div className="amount">
            {Validator.sanitize('number', row.original.Amount, { precision: 2 })} {row.original.Currency}
          </div>
        );
      },
    },
    {
      Header: 'Part',
      accessor: 'SelectedPart',
      className: 'text-center text-bold',
    },
    { Header: 'Coupon', className: 'text-center', accessor: 'CouponCode' },
    { Header: 'Category', className: 'text-center', accessor: 'OfferCategory' },
    { Header: 'Type', className: 'text-center', accessor: 'OfferType' },
  ];

  const getOffers = () => {
    const search = {
      pageSize: 500,
      ...initialSearch,
    };

    setLoading(true);

    const request = initialCustomerId ? fetchCustomerOffers(initialCustomerId, search) : fetchPublicOffers(search);

    request
      .then(response => {
        const { Results } = response.data;

        const offersPerPart = includeApplicableParts(Results);

        setList(offersPerPart);
        setLoading(false);
        setTotal(offersPerPart.length);

        const selectedSeed = { ...selected };

        if (Array.isArray(offersPerPart) && offersPerPart.length > 0) {
          for (const initOffer of initialOffersSelected) {
            const [offerId, ...part] = initOffer.split(':');

            for (const offer of offersPerPart) {
              if (offer.OfferUniqueId === offerId && offer.SelectedPart === part.join('')) {
                selectedSeed[initOffer] = offer;
              }
            }
          }
          setSelected(selectedSeed);
        }
      })
      .catch(e => {
        setLoading(false);

        const { response } = e;

        if (response.status === 404) {
          setTotal(0);
          // setError('No offers for this customer');
        } else {
          setError('Error getting customer offers');
        }
      });
  };

  const renderTable = () => {
    return (
      <ReactTable
        className="-striped -highlight"
        columns={columns}
        data={list}
        loading={loading}
        getTrProps={(state, row) => {
          const isSelected =
            row && row.original && !!selected[`${row.original.OfferUniqueId}:${row.original.SelectedPart}`];
          return {
            className: isSelected ? 'selected' : '',
            onClick: (e, handleOriginal) => {
              toggleRow(row.original);
              if (handleOriginal) {
                handleOriginal();
              }
            },
          };
        }}
        sortable
        showPagination={false}
        style={{
          height: '470px',
        }}
      />
    );
  };

  useEffect(() => {
    setTitle('Select Offers');
    getOffers();
  }, []);

  const renderFooter = () => {
    const primaryAction = (
      <Button
        variant="primary"
        onClick={() => onSelect(Object.keys(selected).map(k => selected[k]))}
        disabled={!selectedCount}
      >
        Apply Selected
      </Button>
    );

    return (
      <div className="footer">
        {selectedCount > 0 && (
          <div className="selected-amount">
            Total <span>{Validator.sanitize('number', getSelectedAmount(), { precision: 2 })}</span>
          </div>
        )}
        <div className="toolbar float-right">{primaryAction}</div>
        <div className="clearfix" />
      </div>
    );
  };

  if (error) {
    return <FileNotFound className="error">{error}</FileNotFound>;
  }

  return (
    <div className="loyalty-list offer-list">
      <div className="current-search">
        <span className="current-label">Applicable Offers</span>
        <div className="total">
          <strong>{loading ? <ClipLoader size={12} /> : total}</strong>
        </div>
      </div>
      {renderTable()}
      {renderFooter()}
    </div>
  );
};

OfferPartList.propTypes = {
  initialCustomerId: PropTypes.string,
  initialOffersSelected: PropTypes.array,
  initialSearch: PropTypes.object,
  onSelect: PropTypes.func,
};

export default OfferPartList;
